/**
 * TicketedEvent Entity Schemas
 *
 * Free Eventbrite-replacement event entity with public RSVP and capacity.
 * Parity expansion (2026-05-09) adds: kind discriminator (ticketed vs
 * volunteer signup), highlights, confirmation customization, and waitlist
 * configuration.
 */

import { z } from 'zod'
import { ContentPageSchema } from './page'
import { ParticipantKindSchema, SignupCloseRuleSchema, SignupRepeatRuleSchema } from './signup-sheet-rule'
import { TicketedEventCategorySchema } from './ticketed-event-category'

export const TicketedEventStatusSchema = z.enum(['draft', 'published', 'unpublished'])
export type TicketedEventStatus = z.infer<typeof TicketedEventStatusSchema>

export const TicketedEventKindSchema = z.enum(['ticketed', 'signup', 'application'])
export type TicketedEventKind = z.infer<typeof TicketedEventKindSchema>

/**
 * The school's brand colors for the two theme modes, as Tailwind HSL token
 * strings ("H S% L%") — the same format `ColorTokenSchema` accepts.
 *
 * Derived server-side from the school's saved app theme so the PUBLIC event
 * surfaces (which signed-out visitors see, outside the dashboard's theme
 * editor) can paint their call-to-action buttons in the school's color instead
 * of the platform default. `primaryForeground` is the theme's own
 * contrast-derived companion to `primary`, so a consumer that applies the pair
 * together is legible by construction; applying one without the other is not.
 *
 * Both modes always travel together: the public pages render inside the
 * dashboard's `next-themes` provider, so the SAME payload has to be able to
 * paint a light and a dark viewer.
 *
 * Deliberately a plain string rather than a stricter HSL schema: a consumer
 * that must not be broken by an unexpected value validates at its own render
 * seam (the web page rejects the whole brand when any token fails), and a
 * strict wire schema would instead fail the entire event parse.
 */
export const TicketedEventBrandModeSchema = z.object({
  primary: z.string(),
  primaryForeground: z.string()
})
export type TicketedEventBrandMode = z.infer<typeof TicketedEventBrandModeSchema>

export const TicketedEventBrandSchema = z.object({
  light: TicketedEventBrandModeSchema,
  dark: TicketedEventBrandModeSchema
})
export type TicketedEventBrand = z.infer<typeof TicketedEventBrandSchema>

export const TicketedEventSchoolBrandingSchema = z.object({
  name: z.string(),
  logoUrlLight: z.string().nullable(),
  logoUrlDark: z.string().nullable(),
  /**
   * The school's IANA timezone (`School.ianaTimeZone`), or null when unset.
   *
   * Public + mobile event surfaces render start times in THIS zone rather than
   * the viewer's device zone. An event happens at a place, so a parent reading
   * the page from another state must see the school's local time, not their own.
   *
   * `.optional()` deliberately, NOT required: the mobile client parses this
   * payload strictly, so a store/OTA build that expected the field would fail
   * the whole parse — blanking the events screen — against any backend that has
   * not deployed it yet. Absent behaves as "no zone on file" and falls back to
   * UTC, the same rule the admin write path uses.
   */
  timezone: z.string().nullable().optional(),
  /**
   * The school's brand colors, or null when the school has no explicit app
   * theme override (an un-branded school keeps the platform default tokens —
   * we must NOT paint it with the system theme's primary as if it had chosen
   * one).
   *
   * `.nullable().optional()` for the same reason `timezone` above is optional:
   * the mobile client parses this payload strictly, so a store/OTA build that
   * required the field would fail the whole parse against any backend that has
   * not deployed it yet. Absent behaves exactly like null — no branding.
   */
  brand: TicketedEventBrandSchema.nullable().optional()
})
export type TicketedEventSchoolBranding = z.infer<typeof TicketedEventSchoolBrandingSchema>

// Public-facing organizer branding (name + optional logo). Shown on the public
// event page's "Hosted by" line when the event has an organizer assigned.
export const TicketedEventPublicOrganizerSchema = z.object({
  name: z.string(),
  logoUrl: z.string().nullable()
})
export type TicketedEventPublicOrganizer = z.infer<typeof TicketedEventPublicOrganizerSchema>

export const TicketedEventHighlightsSchema = z.object({
  ageInfo: z.string().nullable(),
  doorTime: z.string().nullable(),
  parkingInfo: z.string().nullable()
})
export type TicketedEventHighlights = z.infer<typeof TicketedEventHighlightsSchema>

export const TicketedEventConfirmationSchema = z.object({
  replyToEmail: z.string().email().nullable(),
  confirmationMessage: z.string().nullable()
})
export type TicketedEventConfirmation = z.infer<typeof TicketedEventConfirmationSchema>

export const TicketedEventWaitlistConfigSchema = z.object({
  enabled: z.boolean(),
  maxSize: z.number().int(),
  responseHours: z.number().int(),
  autoResponseMessage: z.string().nullable()
})
export type TicketedEventWaitlistConfig = z.infer<typeof TicketedEventWaitlistConfigSchema>

export const TicketedEventSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  startsAt: z.string(),
  durationMinutes: z.number().int(),
  locationText: z.string().nullable(),
  // Coordinates + display name captured from a confirmed address-autocomplete
  // pick (plan 2026-07-20-002, U3). Nullable — text-only events have none.
  // Optional so older cached payloads without the fields stay valid.
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  locationDisplayName: z.string().nullable().optional(),
  coverImageUrl: z.string().nullable(),
  totalCapacity: z.number().int(),
  capacityRemaining: z.number().int(),
  perOrderMax: z.number().int(),
  status: TicketedEventStatusSchema,
  kind: TicketedEventKindSchema,
  highlights: TicketedEventHighlightsSchema,
  confirmation: TicketedEventConfirmationSchema,
  waitlist: TicketedEventWaitlistConfigSchema,
  // Forward-investment (U10): the event's single assigned category, or null.
  // Optional so older cached payloads without the field stay valid.
  category: TicketedEventCategorySchema.nullable().optional(),
  // The event's organizer, derived from its collection's organizer branding
  // (a standalone event, or one whose collection has no organizer, is null).
  // Optional so older cached payloads without the field stay valid.
  organizer: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  // The collection this event is filed into, or null when it stands alone.
  // Carries the TITLE, not just the id `collectionId` below already gives:
  // membership is consequential — a collection event is removed from the
  // school's public events page — and an admin list that shows only an opaque
  // cuid cannot say WHICH collection took it. Optional so older cached
  // payloads without the field stay valid.
  collection: z.object({ id: z.string(), title: z.string() }).nullable().optional(),
  // Display setting (Settings tab). Optional so older cached payloads without
  // the field stay valid; consumers default to no banner.
  //
  // `displayRemainingCount` used to sit here. It never reached a public surface
  // and was off on every event in production, while its admin checkbox promised
  // attendees would see the remaining count. Seat figures are now always shown,
  // so the setting is retired rather than wired up. The Prisma column remains
  // (dropping it is destructive DDL for no gain); nothing reads it.
  salesEndedMessage: z.string().nullable().optional(),
  // Long-form "Overview" page content, stored as a ContentPage block-array
  // (the Puck Custom Pages shape). Null when unset. Optional so older cached
  // payloads without the field stay valid. See plan 2026-07-11-001.
  content: ContentPageSchema.nullable().optional(),
  // True when this event currently holds the school's featured designation —
  // the hero pin on the public events page (U6/R21). Read from the school's
  // single pointer, so at most one event in a school's list is ever true. It
  // says only that the designation EXISTS; whether the hero actually shows
  // this event is re-decided at read time against eligibility (R23).
  // Optional so older cached payloads without the field stay valid.
  isFeatured: z.boolean().optional(),
  // Public id of the collection this event is filed into, or null. Filing an
  // event into a collection takes it OFF the public events page (R27), which
  // also makes it ineligible for the hero (R23) — so the admin surfaces need
  // to know, not just infer. Optional so older cached payloads stay valid.
  collectionId: z.string().nullable().optional(),
  // Recurring signup sheet settings (plan 2026-09-18-0147, U1 / KTD1, KTD9).
  // Every field is optional AND nullable: null means "this sheet does not
  // recur", which is every event in production today, and optional so older
  // cached payloads without the fields stay valid — the same rule the fields
  // above follow.
  //
  // `repeatRule` mints the dated slots; `closeRule` gives each one its cutoff.
  repeatRule: SignupRepeatRuleSchema.nullable().optional(),
  closeRule: SignupCloseRuleSchema.nullable().optional(),
  // Who a registration is for, and how many spots one registrant email may hold
  // across the horizon (R9, R10). A sheet with no participant kind keeps the
  // existing per-email rule (KTD7).
  participantKind: ParticipantKindSchema.nullable().optional(),
  perRegistrantCap: z.number().int().nullable().optional(),
  // How far ahead dates show to registrants (R2), materialized from the rule.
  horizonDays: z.number().int().nullable().optional(),
  // The end of the last generated date, so listing can keep a running sheet in
  // the upcoming window while `startsAt` tracks its next unpassed slot (KTD9).
  repeatEndsAt: z.string().nullable().optional(),
  // Lifecycle (plan 2026-09-09-1309, U12 — R24, KTD11). Publication,
  // cancellation and archival are independent: `status` stays the publication
  // state, and the other two are timestamps. `isCancelled` / `isArchived`
  // restate the timestamps as booleans; `isPast` is derived from the event's
  // dates on every read and never stored. Optional so older cached payloads
  // without the fields stay valid.
  cancelledAt: z.string().nullable().optional(),
  cancelReason: z.string().nullable().optional(),
  archivedAt: z.string().nullable().optional(),
  // After this moment registrants can no longer cancel free registrations
  // themselves (R34, U14). Null means no deadline.
  cancellationDeadlineAt: z.string().nullable().optional(),
  isCancelled: z.boolean().optional(),
  isArchived: z.boolean().optional(),
  isPast: z.boolean().optional(),
  // True once any registration, order or waitlist entry has ever existed for
  // the event, in any status (R27). Such an event cannot be deleted; cancel
  // and archive are the alternatives. Present on the admin list and detail
  // reads only.
  hasRegistrations: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
})
export type TicketedEvent = z.infer<typeof TicketedEventSchema>

export const TicketedEventWaiverRequirementPublicSchema = z.object({
  id: z.string(),
  formPublicId: z.string(),
  // Slug of the linked Form's public page when guest-signable. Null when
  // the form is not currently distributed via public link or doesn't allow
  // anonymous submissions.
  formPublicSlug: z.string().nullable(),
  label: z.string(),
  required: z.boolean(),
  perAttendee: z.boolean(),
  sortOrder: z.number().int()
})
export type TicketedEventWaiverRequirementPublic = z.infer<typeof TicketedEventWaiverRequirementPublicSchema>

export const TicketedEventPublicSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  startsAt: z.string(),
  durationMinutes: z.number().int(),
  locationText: z.string().nullable(),
  // Coordinates + display name for the static-map surfaces (plan
  // 2026-07-20-002, U3/U6). Nullable — text-only events have none. Optional
  // so older cached payloads without the fields stay valid.
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  locationDisplayName: z.string().nullable().optional(),
  coverImageUrl: z.string().nullable(),
  capacityRemaining: z.number().int(),
  perOrderMax: z.number().int(),
  kind: TicketedEventKindSchema,
  highlights: TicketedEventHighlightsSchema,
  confirmation: TicketedEventConfirmationSchema,
  waitlist: TicketedEventWaitlistConfigSchema,
  // Long-form "Overview" page content (ContentPage block-array), or null.
  // Optional so older cached payloads without the field stay valid.
  content: ContentPageSchema.nullable().optional(),
  // The event's organizer branding (own organizer, else its collection's),
  // or null. Optional so older cached payloads without the field stay valid.
  organizer: TicketedEventPublicOrganizerSchema.nullable().optional(),
  // The event's single assigned category, or null (U10/R3). This is what the
  // public category strip derives its options from — the strip offers only the
  // categories actually present among a page's events (R5), so an event
  // carrying null simply contributes no option. Optional so older cached
  // payloads without the field stay valid, matching how `organizer` and
  // `content` were added to this schema.
  category: TicketedEventCategorySchema.nullable().optional(),
  // Present, and only ever `true`, on a recurring signup sheet whose rule has
  // run out (plan 2026-09-18-0147, U1). The link still has to resolve and read
  // as ended rather than 404, so the mapper marks the payload instead of
  // hiding it. Absent on everything else — a consumer never has to learn that
  // `ended: false` is the normal case — hence optional rather than defaulted,
  // which also keeps older cached payloads valid.
  ended: z.literal(true).optional(),
  // A cancelled event's page keeps resolving, because confirmation emails link
  // to it (R25, R48, AE8); the page shows the reason and takes no new
  // registrations. Only the single-event read sets these: public listings
  // leave cancelled events out. Optional so older cached payloads stay valid.
  isCancelled: z.boolean().optional(),
  cancelReason: z.string().nullable().optional(),
  // Derived from the event's dates on every read, never stored (R24).
  isPast: z.boolean().optional(),
  school: TicketedEventSchoolBrandingSchema
})
export type TicketedEventPublic = z.infer<typeof TicketedEventPublicSchema>

/**
 * The public events-hub payload (U6/KTD4).
 *
 * The hero is resolved SERVER-SIDE and carried in `featured`; the client no
 * longer picks `events[0]`. That keeps the eligibility and fallback rules
 * (R22–R24) next to the query that enforces them, and stops the hub and the
 * collection page — which render the same component — from drifting apart.
 *
 * `featured` is NOT repeated in `data`: the two are disjoint, so the grid does
 * not show the hero a second time. Consumers deriving anything "present on the
 * page" (category options, kind tabs, the empty-state gate) must therefore read
 * BOTH, or a school with exactly one eligible event renders a hero above "No
 * upcoming events".
 */
export const TicketedEventPublicHubSchema = z.object({
  school: TicketedEventSchoolBrandingSchema.nullable(),
  // Null when the school has no eligible event at all — the genuine empty state.
  featured: TicketedEventPublicSchema.nullable(),
  data: z.array(TicketedEventPublicSchema)
})
export type TicketedEventPublicHub = z.infer<typeof TicketedEventPublicHubSchema>
